// The role rules of a Custom View's sources, pure (no Amplify client), so the screens, the Ops dashboard and the
// Jest cases share one copy. composedForecastUtils re-exports them.

import { isForecastFilesOutput } from "./seriesSources";

// AWSJSON arrives as a string in some Amplify resolver shapes and as a parsed object in others — accept either and return {} on missing/invalid input.
export function parseMetadata(metadata: unknown): Record<string, any> {
  if (!metadata) return {};
  if (typeof metadata === "object") return metadata as Record<string, any>;
  if (typeof metadata !== "string") return {};
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
}

/** A source's place in the Custom View list (metadata.drawOrder on a saved source, drawOrder on a form row); null when never saved with one. */
export const sourceDrawOrder = (ds: any): number | null => {
  const order = ds?.drawOrder ?? parseMetadata(ds?.metadata)?.drawOrder;
  return typeof order === "number" && Number.isFinite(order) ? order : null;
};

/**
 * The sources in the order the Custom View form lists them. The list order is also the draw order (the top row is
 * drawn on top) and decides which forecast is the base, so every screen sorts through here. Sources saved before
 * drawOrder existed keep the order they came in, after the ordered ones.
 */
export const orderedSources = <T>(sources: T[] | null | undefined): T[] =>
  (sources || [])
    .map((ds, index) => ({ ds, index, order: sourceDrawOrder(ds) }))
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity) || a.index - b.index)
    .map(({ ds }) => ds);

/** Days of history a view shows before its forecast starts (the base source's metadata.historyDays); null = as long as the forecast. */
export const viewHistoryDays = (baseSource: any): number | null => {
  const days = baseSource?.historyDays ?? parseMetadata(baseSource?.metadata)?.historyDays;
  return typeof days === "number" && days > 0 ? days : null;
};

/** Saved sources carry the observation flag in metadata.accuracy; form rows carry it as `accuracy`. */
export const savedObservationFlag = (ds: any): boolean => {
  const accuracy = parseMetadata(ds?.metadata)?.accuracy;
  return !!(accuracy?.enabled && accuracy.mode);
};
// Form rows only need `enabled`: the "Use as observation" toggle always writes a complete config (mode included,
// from DEFAULT_ACCURACY_CONFIG), whereas saved metadata may come from anywhere, so it is checked for `mode` too.
export const formObservationFlag = (ds: any): boolean => !!ds?.accuracy?.enabled;

/**
 * Which source is the base model (the forecast that is charted with bands and scored) and which is the
 * observation — by ROLE, never by position: DynamoDB returns a composed forecast's sources unordered, and they
 * are created in parallel, so "the first model output" is not stable between saving and loading.
 * - observation: the flagged source — unless it is the only model output (a model can't score itself, #908);
 * - base model: the first PROCESSED_MODEL_OUTPUT that isn't the observation. A range source (a curated dataset,
 *   stage 5) is never the base: it is a different sourceType.
 */
export function resolveSourceRoles(
  dataSources: any[],
  isFlagged: (ds: any) => boolean = savedObservationFlag
): { baseIndex: number; observationIndex: number } {
  const sources = dataSources || [];
  const models = sources.map((ds, i) => (isForecastOutputSource(ds) ? i : -1)).filter((i) => i >= 0);
  let observationIndex = sources.findIndex((ds) => isFlagged(ds));
  if (observationIndex >= 0 && models.length === 1 && models[0] === observationIndex) observationIndex = -1;
  const baseIndex = models.find((i) => i !== observationIndex) ?? -1;
  return { baseIndex, observationIndex };
}

/** A model-output source: a forecast with dated issues (the picker offers FILES outputs only). */
export const isForecastOutputSource = (ds: any): boolean =>
  ds?.sourceType === "PROCESSED_MODEL_OUTPUT" && isForecastFilesOutput(ds?.processedModelOutput);

/** Form roles — the same rules applied to unsaved form rows. */
export const resolveFormSourceRoles = (dataSources: any[]) => resolveSourceRoles(dataSources, formObservationFlag);

/** The fields a form row needs before it can be read: the same rule gates the modal's save and the row's Preview. */
export interface SourceConfigurationFields {
  sourceType?: string;
  parameterId?: string | number;
  processedModelOutputId?: string;
  column?: string;
  curated?: boolean;
  datasetId?: string;
  datasetRecordS3Path?: string;
}

/**
 * What a source row still needs, or null when it is complete. An incomplete row must not be saved: a range source
 * without its column, or a dataset without its record, is dropped from the chart and the Ops dashboard with no
 * message, so the modal refuses the save and names the missing field instead (PR #948 review).
 */
export function sourceConfigurationError(ds: SourceConfigurationFields | null | undefined): string | null {
  switch (ds?.sourceType) {
    case "POSTGRES_HISTORICAL":
      return ds.parameterId ? null : "Pick a parameter.";
    case "PROCESSED_MODEL_OUTPUT":
      return ds.processedModelOutputId ? null : "Pick a model output.";
    case "DATASET":
      if (!ds.datasetId) return "Pick a dataset.";
      if (ds.curated) return ds.column ? null : "Pick a column. This dataset has no columns recorded yet, so it can't be drawn.";
      return ds.datasetRecordS3Path ? null : "Pick a dataset record.";
    default:
      return "Pick a source type.";
  }
}

export const isSourceConfigured = (ds: SourceConfigurationFields | null | undefined): boolean =>
  sourceConfigurationError(ds) === null;
